import React, { useMemo, Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Component as ComponentIcon, CheckCircle2, Hash, Image, Ruler, PieChart, Grid3x3, ArrowLeftRight, Table, Lightbulb, Coins, Columns, Clock, Grid2x2, BookOpen, Scissors, SquareStack, Circle, Crosshair, Shapes, BoxIcon } from 'lucide-react';
import { StatCard } from '../../../shared/StatCard';
import { Card } from '../../../../components/ui/Card';
import { Question } from '../../../../content';
import { VISUAL_REGISTRY } from '../../../../visuals';
// Color map for module badges
const MODULE_BADGE_COLORS: Record<string, {
  dot: string;
}> = {
  GET: {
    dot: 'bg-blue-500'
  },
  MET: {
    dot: 'bg-green-500'
  },
  SPR: {
    dot: 'bg-purple-500'
  },
  SWW: {
    dot: 'bg-indigo-500'
  },
  TAA: {
    dot: 'bg-pink-500'
  },
  TGL: {
    dot: 'bg-orange-500'
  },
  TOPO: {
    dot: 'bg-teal-500'
  },
  STU: {
    dot: 'bg-cyan-500'
  }
};
const VISUAL_ICONS: Record<string, React.ElementType> = {
  stairs: Ruler,
  fraction: PieChart,
  geo: Grid3x3,
  numberline: ArrowLeftRight,
  ratio: Table,
  rule: Lightbulb,
  money: Coins,
  column: Columns,
  clock: Clock,
  decimalgrid: Grid2x2,
  verbTable: BookOpen,
  kofschipMnemonic: Scissors,
  syllableBlocks: SquareStack,
  circle: Circle,
  coordinateGrid: Crosshair,
  box3d: BoxIcon,
  compositeShape: Shapes,
  geometricShape: Shapes,
  barChart: Columns
};
interface VisualsTabProps {
  questions: Question[];
  onSelectVisual: (visualType: string) => void;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 12
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 28
    }
  }
};
function ModuleBadge({
  code
}: {
  code: string;
}) {
  const colors = MODULE_BADGE_COLORS[code] || {
    dot: 'bg-gray-500'
  };
  return <span className="text-[10px] font-bold px-2 py-1 rounded-md border bg-gray-50 border-gray-200 text-gray-700 inline-flex items-center gap-1" title={code} data-id="element-3607">
      <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} data-id="element-3608" />
      {code}
    </span>;
}
export function VisualsTab({
  questions,
  onSelectVisual
}: VisualsTabProps) {
  const registeredVisuals = useMemo(() => Object.keys(VISUAL_REGISTRY).sort(), []);
  // Build usage stats for each visual
  const visualUsage = useMemo(() => {
    const usage: Record<string, {
      count: number;
      stimulusCount: number;
      supportCount: number;
      families: Set<string>;
      modules: Set<string>;
      questions: Question[];
    }> = {};
    registeredVisuals.forEach(v => {
      usage[v] = {
        count: 0,
        stimulusCount: 0,
        supportCount: 0,
        families: new Set(),
        modules: new Set(),
        questions: []
      };
    });
    questions.forEach(q => {
      const moduleCode = q.familyId?.split('-')[0] || 'UNK';
      const usedTypes = new Set<string>();
      q.stimulusAids?.forEach(aid => {
        if (aid.type && usage[aid.type]) {
          usage[aid.type].count++;
          usage[aid.type].stimulusCount++;
          usage[aid.type].families.add(q.familyId || '');
          usage[aid.type].modules.add(moduleCode);
          usedTypes.add(aid.type);
        }
      });
      q.supportAids?.forEach(aid => {
        if (aid.type && usage[aid.type]) {
          usage[aid.type].count++;
          usage[aid.type].supportCount++;
          usage[aid.type].families.add(q.familyId || '');
          usage[aid.type].modules.add(moduleCode);
          usedTypes.add(aid.type);
        }
      });
      usedTypes.forEach(type => {
        if (usage[type]) usage[type].questions.push(q);
      });
    });
    return usage;
  }, [registeredVisuals, questions]);
  // Sort by usage
  const sortedVisuals = useMemo(() => [...registeredVisuals].sort((a, b) => visualUsage[b].count - visualUsage[a].count), [registeredVisuals, visualUsage]);
  // Stats
  const activeVisuals = sortedVisuals.filter(v => visualUsage[v].count > 0).length;
  const totalReferences = Object.values(visualUsage).reduce((sum, u) => sum + u.count, 0);
  return <div className="space-y-6" data-id="element-3609">
      {/* Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-3610">
        <motion.div variants={itemVariants} data-id="element-3611">
          <StatCard label="Geregistreerde Visuals" value={registeredVisuals.length} icon={<ComponentIcon className="w-5 h-5" data-id="element-3613" />} color="purple" data-id="element-3612" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3614">
          <StatCard label="Actief in Gebruik" value={activeVisuals} icon={<CheckCircle2 className="w-5 h-5" data-id="element-3616" />} color="emerald" data-id="element-3615" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3617">
          <StatCard label="Totale Referenties" value={totalReferences.toLocaleString('nl-NL')} icon={<Hash className="w-5 h-5" data-id="element-3619" />} color="blue" data-id="element-3618" />
        </motion.div>
      </motion.div>

      {/* Visual Cards Grid */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3620">
        {sortedVisuals.map(v => {
        const usage = visualUsage[v];
        const isUsed = usage.count > 0;
        const Icon = VISUAL_ICONS[v] || Image;
        return <motion.div key={v} variants={itemVariants} data-id="element-3621">
              <Card className={`p-5 h-full flex flex-col transition-all duration-200 cursor-pointer ${isUsed ? 'hover:shadow-md hover:border-purple-300 border-gray-200' : 'opacity-50 border-gray-100 bg-gray-50/50 cursor-not-allowed'}`} onClick={() => isUsed && onSelectVisual(v)} data-id="element-3622">
                <div className="flex items-start justify-between mb-4" data-id="element-3623">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isUsed ? 'bg-purple-100 text-purple-600' : 'bg-gray-200 text-gray-400'}`} data-id="element-3624">
                    <Icon className="w-5 h-5" data-id="element-3625" />
                  </div>
                  <div className="text-right" data-id="element-3626">
                    <div className={`text-xl font-black ${isUsed ? 'text-purple-600' : 'text-gray-400'}`} data-id="element-3627">
                      {usage.count}
                    </div>
                    <div className="text-[10px] text-gray-500 font-medium" data-id="element-3628">
                      referenties
                    </div>
                  </div>
                </div>

                <div className="mb-4" data-id="element-3629">
                  <code className="text-sm font-mono font-bold text-gray-900 block mb-2" data-id="element-3630">
                    {v}
                  </code>
                  {isUsed && <div className="space-y-1.5" data-id="element-3631">
                      <div className="flex justify-between text-[10px] text-gray-500" data-id="element-3632">
                        <span data-id="element-3633">Stimulus ({usage.stimulusCount})</span>
                        <span data-id="element-3634">Support ({usage.supportCount})</span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden flex" data-id="element-3635">
                        <div className="bg-blue-400 h-full" style={{
                    width: `${usage.stimulusCount / usage.count * 100}%`
                  }} data-id="element-3636" />
                        <div className="bg-amber-400 h-full" style={{
                    width: `${usage.supportCount / usage.count * 100}%`
                  }} data-id="element-3637" />
                      </div>
                    </div>}
                </div>

                <div className="mt-auto pt-3 border-t border-gray-100" data-id="element-3638">
                  <div className="flex flex-wrap gap-1.5" data-id="element-3639">
                    {usage.modules.size > 0 ? Array.from(usage.modules).sort().slice(0, 4).map(m => <ModuleBadge key={m} code={m} data-id="element-3640" />) : <span className="text-xs text-gray-400 italic" data-id="element-3641">
                        Niet in gebruik
                      </span>}
                    {usage.modules.size > 4 && <span className="text-[10px] text-gray-400 font-medium px-1.5 py-0.5" data-id="element-3642">
                        +{usage.modules.size - 4}
                      </span>}
                  </div>
                </div>
              </Card>
            </motion.div>;
      })}
      </motion.div>
    </div>;
}